import { useCallback, useEffect, useRef, useState } from "react";
import axios from "./axios";

/**
 * RECHERCHE D'ADRESSE — back-office.
 *
 * POURQUOI CE FICHIER N'EXISTAIT PAS, ET POURQUOI C'EST UN PROBLEME
 * -----------------------------------------------------------------
 * L'API, l'application mobile et l'application flotte partagent depuis peu une
 * seule chaine : repertoire interne (`sign` + `address`) d'abord, fournisseur
 * externe en repli COTE SERVEUR, persistance du lieu retenu. Le back-office,
 * lui, n'avait rien : dans ModalAddSign comme dans ModalAddPub, l'operateur
 * SAISIT LA LATITUDE ET LA LONGITUDE A LA MAIN.
 *
 * C'est le seul endroit de la plateforme ou l'on cree les lignes `sign` que
 * toutes les autres recherches vont ensuite lire. Une coordonnee fausse saisie
 * ici se propage partout, et rien en aval ne peut la rattraper.
 *
 * Le detail qui rend la saisie manuelle dangereuse plutot que simplement
 * penible : `CustomAddressInput` faisait `parseFloat(value) || 0`. Un champ
 * laisse vide devenait donc 0 — pas NULL. Or (0, 0) est un point REEL, au large
 * du golfe de Guinee. Une enseigne enregistree ainsi n'est pas \u00ab sans
 * position \u00bb : elle est positionnee dans l'Atlantique, invisible sur la carte
 * et hors de portee de toute recherche par proximite. Et depuis l'ajout du
 * ciblage publicitaire par rayon, une campagne a (0, 0) n'est pas nationale non
 * plus — elle est ciblee sur l'ocean, donc jamais diffusee a personne.
 */

export interface PlaceSuggestion {
  /** Identifiant stable : 'sign:<id>' ou 'google:<place_id>'. */
  id: string;
  name: string;
  raw?: string | null;
  /** Ligne ville/region, uniquement sur un resultat externe. */
  secondary?: string | null;
  brand?: string | null;
  category?: string | null;
  phone?: string | null;
  centre_code?: string | null;
  sign_id?: number;
  address_id?: number | null;
  /** Id fournisseur, uniquement sur un resultat externe. */
  place_id?: string;
  lat?: number | null;
  lng?: number | null;
  distance_km?: number | null;
  source: "internal" | "external";
}

export interface PlaceSearchResult {
  data: PlaceSuggestion[];
  /**
   * Credit du fournisseur, a afficher verbatim sous les resultats externes.
   * Non nul UNIQUEMENT quand ils viennent du fournisseur.
   */
  attribution: string | null;
}

const EMPTY: PlaceSearchResult = { data: [], attribution: null };

/** Le serveur ignore les requetes plus courtes. */
export const MIN_PLACE_QUERY = 2;

/** Anti-rebond des frappes, aligne sur les autres clients. */
const DEBOUNCE_MS = 300;

/**
 * Bascule MEMORISEE vers les anciennes URL apres un 404.
 *
 * Le back-office peut etre deploye avant l'API. Sans repli, la recherche serait
 * morte sans message ; sans memorisation, chaque frappe paierait un 404 avant
 * de retomber sur l'URL qui repond.
 */
let useLegacyEndpoints = false;

function isNotFound(error: any): boolean {
  return error?.response?.status === 404;
}

/**
 * Cherche un lieu. Ne rejette jamais : une recherche en echec renvoie une liste
 * vide, car l'operateur doit pouvoir continuer sa saisie a la main.
 */
export async function searchPlaces(
  query: string,
  coords?: { lat: number; lng: number } | null
): Promise<PlaceSearchResult> {
  const q = (query ?? "").trim();
  if (q.length < MIN_PLACE_QUERY) return EMPTY;

  const params: Record<string, string> = { q };

  // Paire COMPLETE uniquement, et (0, 0) rejete — voir l'en-tete de ce fichier.
  if (
    coords &&
    Number.isFinite(coords.lat) &&
    Number.isFinite(coords.lng) &&
    !(coords.lat === 0 && coords.lng === 0)
  ) {
    params.lat = String(coords.lat);
    params.lng = String(coords.lng);
  }

  const call = (url: string) =>
    axios.get(url, { params }).then((res) => ({
      data: Array.isArray(res?.data?.data) ? (res.data.data as PlaceSuggestion[]) : [],
      attribution: res?.data?.attribution ?? null,
    }));

  try {
    return await call(useLegacyEndpoints ? "/event/address/search" : "/place/search");
  } catch (error) {
    if (!useLegacyEndpoints && isNotFound(error)) {
      useLegacyEndpoints = true;
      try {
        return await call("/event/address/search");
      } catch {
        return EMPTY;
      }
    }
    return EMPTY;
  }
}

/**
 * Resout un lieu externe : recupere ses coordonnees ET l'enregistre en base.
 *
 * Etape OBLIGATOIRE sur un resultat externe — l'autocompletion du fournisseur
 * ne renvoie aucune geometrie. Idempotent cote serveur (index unique sur le
 * place id).
 */
export async function resolvePlace(placeId: string): Promise<PlaceSuggestion | null> {
  const call = (url: string) =>
    axios.post(url, { place_id: placeId }).then((res) => (res?.data ?? null) as PlaceSuggestion | null);

  try {
    return await call(useLegacyEndpoints ? "/event/address/resolve" : "/place/resolve");
  } catch (error) {
    if (!useLegacyEndpoints && isNotFound(error)) {
      useLegacyEndpoints = true;
      try {
        return await call("/event/address/resolve");
      } catch {
        return null;
      }
    }
    return null;
  }
}

export interface UsePlaceSearchResult {
  suggestions: PlaceSuggestion[];
  attribution: string | null;
  isSearching: boolean;
  isResolving: boolean;
  /** A appeler a chaque frappe. Anti-rebond integre. */
  search: (query: string) => void;
  /** Resout une suggestion en lieu complet (coordonnees garanties ou null). */
  resolve: (place: PlaceSuggestion) => Promise<PlaceSuggestion | null>;
  clear: () => void;
}

/**
 * Hook partage par tous les formulaires du back-office qui saisissent une
 * adresse.
 *
 * Il porte les deux protections que chaque formulaire aurait sinon du
 * reimplementer — et qu'aucun n'aurait implementees de la meme facon :
 *
 *  - L'ANTI-REBOND, pour ne pas envoyer une requete par caractere.
 *  - LE NUMERO DE SEQUENCE. Seule la reponse la plus recente a le droit de
 *    s'afficher. L'anti-rebond seul ne suffit pas : deux requetes espacees de
 *    400 ms peuvent revenir dans le desordre, et la liste montre alors les
 *    resultats d'un prefixe deja fini de taper. Ce bug ne se reproduit que sur
 *    reseau lent, donc jamais au bureau.
 */
export function usePlaceSearch(coords?: { lat: number; lng: number } | null): UsePlaceSearchResult {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [attribution, setAttribution] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const clear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestSeq.current++;
    setSuggestions([]);
    setAttribution(null);
    setIsSearching(false);
  }, []);

  const search = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if ((query ?? "").trim().length < MIN_PLACE_QUERY) {
        clear();
        return;
      }

      setIsSearching(true);

      debounceRef.current = setTimeout(async () => {
        const seq = ++requestSeq.current;
        const result = await searchPlaces(query, coords);

        if (!mounted.current || seq !== requestSeq.current) return;

        setSuggestions(result.data);
        setAttribution(result.attribution);
        setIsSearching(false);
      }, DEBOUNCE_MS);
    },
    [coords, clear]
  );

  const resolve = useCallback(async (place: PlaceSuggestion): Promise<PlaceSuggestion | null> => {
    // Un lieu interne est deja complet : aucun aller-retour.
    if (place.source === "internal" || !place.place_id) return place;

    setIsResolving(true);
    try {
      return await resolvePlace(place.place_id);
    } finally {
      if (mounted.current) setIsResolving(false);
    }
  }, []);

  return { suggestions, attribution, isSearching, isResolving, search, resolve, clear };
}
